import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Empty } from 'antd'
import { PlusCircleFilled } from '@ant-design/icons'
import Button from '@UI/New/Button'
import Pagination from '@UI/New/Pagination'
import Preloader from '@UI/Preloader'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import Select from '@pages/Admin/CRM/components/base/Select/Select'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useCRMContext } from '@pages/Admin/CRM/CRMContext'
import TaskTrackerTaskDrawer from '@pages/Admin/TaskTrackerCRM/components/TaskTrackerTaskDrawer/TaskTrackerTaskDrawer'
import { taskTrackerApi } from '@pages/Admin/TaskTrackerCRM/api/taskTracker.api'
import type { TaskTrackerBoardCard } from '@pages/Admin/TaskTrackerCRM/store/taskTracker.types'
import LeadProfileModalBlock from '../../components/LeadProfileModalBlock/LeadProfileModalBlock'
import { useLeadRefetch } from '../../LeadRefetchContext'
import useFullLeadModalAccess from '../../hooks/useFullLeadModalAccess'
import LeadTasksTable from './LeadTasksTable'
import CreateLeadTaskModal from './CreateLeadTaskModal'
import { useLeadTasks } from './useLeadTasks'
import {
  canCreateLeadTask,
  CRM_LEAD_TASKS_STATUS_OPTIONS,
  parseCrmLeadTasksStatus,
} from './leadTasks.utils'
import classes from './index.module.css'

/** Выше модалки карточки лида (103) и create-task (110). */
const LEAD_TASK_DRAWER_Z_INDEX = 120

const ADD_TASK_NEEDS_MANAGER_TOOLTIP =
  'Чтобы добавить задачу, выберите менеджера'

type TasksContentProps = {
  multipleDealsEnabled?: boolean
}

const TasksContent: FC<TasksContentProps> = ({
  multipleDealsEnabled = false,
}) => {
  const addAlert = useAddAlert()
  const { preloadingData } = useCRMContext()
  const {
    lead,
    resolveLeadFunnelId,
    committedManagerId,
    selectedFunnelId,
    multipleDealsEnabled: dealsOn,
  } = useLeadRefetch()
  const { currentRole } = useFullLeadModalAccess()
  const [createOpen, setCreateOpen] = useState(false)

  const workspaceRef = useRef<HTMLElement | null>(null)
  const [drawerTaskId, setDrawerTaskId] = useState<number | null>(null)
  const [drawerTask, setDrawerTask] = useState<TaskTrackerBoardCard | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)

  const leadFunnelId = resolveLeadFunnelId()
  const showDealScope = multipleDealsEnabled || dealsOn
  const canCreate = canCreateLeadTask(currentRole)
  const hasLeadFunnel =
    leadFunnelId != null &&
    Number.isFinite(leadFunnelId) &&
    leadFunnelId > 0
  const hasManager =
    committedManagerId != null &&
    Number.isFinite(committedManagerId) &&
    committedManagerId > 0
  const canOpenCreate = hasLeadFunnel && hasManager
  const addTaskDisabledReason =
    hasLeadFunnel && !hasManager ? ADD_TASK_NEEDS_MANAGER_TOOLTIP : null

  const {
    items,
    counts,
    loading,
    creating,
    createTask,
    reloadSilent,
    status,
    setStatus,
    currentPage,
    perPage,
    totalPages,
    setCurrentPage,
    setPerPage,
    nextPage,
    prevPage,
  } = useLeadTasks({
    leadId: lead?.id,
    funnelId: selectedFunnelId,
    leadFunnelId,
    enabled: hasLeadFunnel,
  })

  useEffect(() => {
    workspaceRef.current = document.documentElement
  }, [])

  useEffect(() => {
    if (canOpenCreate) return
    setCreateOpen(false)
  }, [canOpenCreate])

  useEffect(() => {
    if (drawerTaskId == null) {
      setDrawerTask(null)
      setDrawerLoading(false)
      return
    }

    let cancelled = false
    setDrawerLoading(true)

    void taskTrackerApi
      .getTask(drawerTaskId)
      .then((task) => {
        if (cancelled) return
        setDrawerTask(task)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setDrawerTask(null)
        setDrawerTaskId(null)
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text:
            error instanceof Error && error.message
              ? error.message
              : 'Не удалось открыть задачу',
        })
      })
      .finally(() => {
        if (!cancelled) setDrawerLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [addAlert, drawerTaskId])

  const handleCloseDrawer = useCallback(() => {
    setDrawerTaskId(null)
    setDrawerTask(null)
  }, [])

  const handleDrawerTaskUpdated = useCallback(
    (task: TaskTrackerBoardCard) => {
      setDrawerTask(task)
      void reloadSilent()
    },
    [reloadSilent],
  )

  const handleDrawerTaskDeleted = useCallback(() => {
    handleCloseDrawer()
    void reloadSilent()
  }, [handleCloseDrawer, reloadSilent])

  const addTaskButton = canCreate ? (
    <Button
      variant="action"
      color="secondary"
      disabled={!canOpenCreate}
      onClick={() => setCreateOpen(true)}
      leftContent={
        <PlusCircleFilled
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      }
    >
      Добавить задачу
    </Button>
  ) : null

  const headerRight =
    addTaskButton == null
      ? null
      : addTaskDisabledReason != null
        ? (
            <TooltipWrapper title={addTaskDisabledReason} placement="top">
              <span className={classes.addTaskButtonWrap}>{addTaskButton}</span>
            </TooltipWrapper>
          )
        : addTaskButton

  const countsHint =
    counts.overdue > 0 || counts.today > 0
      ? [
          counts.overdue > 0 ? `просрочено: ${counts.overdue}` : null,
          counts.today > 0 ? `на сегодня: ${counts.today}` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null

  const emptyListDescription = showDealScope
    ? 'Задач по этой сделке пока нет'
    : 'Задач пока нет'

  return (
    <>
      <LeadProfileModalBlock
        aria-label="Задачи"
        title="Задачи"
        titleHint={showDealScope ? 'по сделке' : undefined}
        headerRight={headerRight}
      >
        {hasLeadFunnel ? (
          <div className={classes.toolbar}>
            <Select
              size="small"
              className={classes.statusFilterSelect}
              options={CRM_LEAD_TASKS_STATUS_OPTIONS}
              value={status}
              onChange={(value) => {
                setStatus(parseCrmLeadTasksStatus(value))
              }}
              placeholder="Статус задач"
            />
            {countsHint ? (
              <div className={classes.countsRow}>{countsHint}</div>
            ) : null}
          </div>
        ) : null}

        {!hasLeadFunnel ? (
          <p className={classes.empty}>
            {showDealScope
              ? 'Не выбрана сделка — задачи недоступны.'
              : 'Нет связки лид–воронка — задачи недоступны.'}
          </p>
        ) : loading && items.length === 0 ? (
          <div className={classes.preloaderWrapper}>
            <Preloader />
          </div>
        ) : items.length === 0 ? (
          <div className={classes.emptyWrap}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={emptyListDescription}
            />
          </div>
        ) : (
          <div className={classes.list}>
            <LeadTasksTable
              tasks={items}
              onEdit={setDrawerTaskId}
              onChanged={() => {
                void reloadSilent()
              }}
            />
          </div>
        )}

        {hasLeadFunnel ? (
          <div className={classes.footerBar}>
            <div className={classes.paginationGrow}>
              <Pagination
                perPage={perPage}
                setPerPage={setPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                nextPage={nextPage}
                prevPage={prevPage}
                totalPages={totalPages}
              />
            </div>
          </div>
        ) : null}
      </LeadProfileModalBlock>

      {canOpenCreate && canCreate ? (
        <CreateLeadTaskModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          leadFunnelId={leadFunnelId}
          managers={preloadingData.managers ?? []}
          committedCrmManagerId={committedManagerId}
          role={currentRole}
          submitting={creating}
          onSubmit={createTask}
        />
      ) : null}

      <TaskTrackerTaskDrawer
        open={drawerTaskId != null}
        loading={drawerLoading}
        task={drawerTask}
        projectId={null}
        workspaceRef={workspaceRef}
        zIndex={LEAD_TASK_DRAWER_Z_INDEX}
        onClose={handleCloseDrawer}
        onTaskUpdated={handleDrawerTaskUpdated}
        onTaskDeleted={handleDrawerTaskDeleted}
      />
    </>
  )
}

export default TasksContent
